import { useCallback, useEffect, useMemo, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from "@mui/material";

import { ArrowDownIcon } from "../../Icons/Icons";
import ResponsiveDataGrid from "../DataGrid/ResponsiveDataGrid";

export function ResponsiveSummaryTable({
  rows = [],
  columns = [],
  grayCategories = ["Sold", "Unpriced Total", "Marketing Total", "Profit"],
  allExpanded: allExpandedProp = false,
  onToggleAll,
  getRowClassName,
  mobileCardProps = {},
  hidePagination = true,
}) {
  const [expandedRows, setExpandedRows] = useState({});

  const allRowIds = useMemo(() => {
    const filteredRows = rows.filter((row) => !row.isSeparator);
    return filteredRows.map((row) => row.id);
  }, [rows]);

  const handleAccordionChange = useCallback(
    (rowId) => (event, isExpanded) => {
      setExpandedRows((prev) => {
        const newExpandedRows = {
          ...prev,
          [rowId]: isExpanded,
        };

        const allExpandedState = allRowIds.every((id) => newExpandedRows[id] === true);
        const allCollapsedState = allRowIds.every(
          (id) => newExpandedRows[id] === false || newExpandedRows[id] === undefined
        );

        if (onToggleAll) {
          if (allExpandedState) {
            onToggleAll(true);
          } else if (allCollapsedState) {
            onToggleAll(false);
          }
        }

        return newExpandedRows;
      });
    },
    [allRowIds, onToggleAll]
  );

  useEffect(() => {
    const newExpandedRows = {};
    allRowIds.forEach((id) => {
      newExpandedRows[id] = allExpandedProp;
    });
    setExpandedRows(newExpandedRows);
  }, [allExpandedProp, allRowIds]);

  const renderMobileCard = useCallback(
    (row, columns) => {
      const rowId = row.id;
      const isExpanded = expandedRows[rowId] === true;
      const categoryValue = row.category || row[columns[0]?.field] || "";
      const revenueValue = row.revenue || row[columns[columns.length - 1]?.field] || "";

      const shouldBeGray = grayCategories.includes(categoryValue);
      const backgroundColor = shouldBeGray ? "grey.200" : "background.paper";

      const rowClassName = getRowClassName ? getRowClassName(row) : "";

      return (
        <Accordion
          key={rowId}
          expanded={isExpanded}
          onChange={handleAccordionChange(rowId)}
          sx={{
            boxShadow: "none",
            "&:before": { display: "none" },
            borderBottom: "1px solid",
            borderColor: "divider",
            margin: 0,
            "&.Mui-expanded": { margin: 0 },
            backgroundColor: backgroundColor,
            "&:hover": {
              backgroundColor: shouldBeGray ? "grey.200" : "grey.100",
            },
            ...mobileCardProps.sx,
          }}
          className={rowClassName}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: 48,
              "&.Mui-expanded": { minHeight: 48 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",

                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ArrowDownIcon
                  sx={{
                    fontSize: "1.25rem",
                    color: "inherit",
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {categoryValue}
                </Typography>
              </Box>
              <Typography variant="body1" align="right" sx={{ fontWeight: 500 }}>
                {revenueValue}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {columns.map((col) => {
                if (!col || col.field === "category" || col.field === "revenue") return null;
                const value = row[col.field] ?? "-";
                return (
                  <Box key={col.field} sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "0.875rem" }}>
                      {col.headerName}:
                    </Typography>
                    <Typography variant="body1" align="right" sx={{ fontSize: "0.875rem" }}>
                      {value}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      );
    },
    [columns, expandedRows, handleAccordionChange, grayCategories, getRowClassName, mobileCardProps]
  );

  return (
    <Box
      sx={{
        "& .responsive-data-grid-stack": {
          gap: 0,
          "& > *": {
            marginTop: "0 !important",
            marginBottom: "0 !important",
          },
        },
      }}
    >
      <ResponsiveDataGrid
        rows={rows.filter((row) => !row.isSeparator)}
        columns={columns}
        paginationModel={{ page: 0, pageSize: 10000 }}
        pageSizeOptions={[]}
        hidePagination={hidePagination}
        hideFooter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        renderCard={renderMobileCard}
      />
    </Box>
  );
}
